// Gemerkte Listenansichten, Spaltenkatalog und berechnete Spaltenwerte für die Lead-Listen (Agent 5B, Runde 5).
// - useGemerkteAnsicht(liste): Spalten, Sortierung und Zusätze des Nutzers für eine Liste; Änderungen erscheinen sofort und werden
//   im Hintergrund gespeichert (schlägt das Speichern fehl, bleibt die Liste benutzbar, die Wahl gilt dann nur bis zum Neuladen).
// - useFelderKatalog(): alle wählbaren Spalten (einmal je Sitzung geholt).
// - useBerechneteWerte(): Werte wie „Anzahl Anrufe“ für die gerade sichtbaren Leads (in Paketen geholt, gemerkt, bei Änderungen des Leads verworfen).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEchtzeit } from "../api/echtzeit";
import { listenAnsichtenApi, type AnsichtEingabe, type FelderKatalog, type GemerkteAnsicht } from "../api/listenAnsichten";
import { beimNutzerwechsel } from "./sitzung";

// ---------------------------------------------------------------------------
// Gemerkte Ansicht
// ---------------------------------------------------------------------------

const ansichten = new Map<string, GemerkteAnsicht | null>();
const ladend = new Map<string, Promise<GemerkteAnsicht | null>>();
const genutztAm = new Map<string, number>();
let katalog: FelderKatalog | null = null;
let katalogLadend: Promise<FelderKatalog | null> | null = null;
const werte = new Map<string, Record<string, unknown>>();
const angefragt = new Set<string>();
const werteHoerer = new Set<() => void>();
let werteStand = 0;
/** Zählt Nutzerwechsel: Antworten aus einer früheren Sitzung werden verworfen. */
let sitzung = 0;

beimNutzerwechsel(() => {
  sitzung++;
  ansichten.clear();
  ladend.clear();
  genutztAm.clear();
  katalog = null;
  katalogLadend = null;
  werte.clear();
  angefragt.clear();
  werteStand++;
  werteHoerer.forEach((h) => h());
});

function laden(liste: string): Promise<GemerkteAnsicht | null> {
  const vorhanden = ladend.get(liste);
  if (vorhanden) return vorhanden;
  const meine = sitzung;
  const p = listenAnsichtenApi
    .eine(liste)
    .then((r) => {
      if (meine === sitzung) ansichten.set(liste, r.ansicht);
      return r.ansicht;
    })
    .catch(() => null)
    .finally(() => {
      if (meine === sitzung) ladend.delete(liste);
    });
  ladend.set(liste, p);
  return p;
}

function eingemischt(alt: GemerkteAnsicht | null, liste: string, teil: AnsichtEingabe): GemerkteAnsicht {
  const basis: GemerkteAnsicht = alt ?? { liste, spalten: null, sortierung: null, einstellungen: {}, zuletzt_genutzt_at: null, nutzungen: 0, date_updated: null };
  const einstellungen = { ...basis.einstellungen };
  if (teil.einstellungen === null) for (const k of Object.keys(einstellungen)) delete einstellungen[k];
  else if (teil.einstellungen) for (const [k, w] of Object.entries(teil.einstellungen)) (w === null ? delete einstellungen[k] : (einstellungen[k] = w));
  return {
    ...basis,
    spalten: "spalten" in teil ? (teil.spalten ?? null) : basis.spalten,
    sortierung: "sortierung" in teil ? (teil.sortierung ?? null) : basis.sortierung,
    einstellungen,
  };
}

export interface AnsichtZugriff {
  /** gemerkte Ansicht des Nutzers; null = Standard der Liste */
  ansicht: GemerkteAnsicht | null;
  /** false, solange die gemerkte Ansicht noch geholt wird (dann nicht vorschnell den Standard zeigen) */
  geladen: boolean;
  /** Teile merken (sofort sichtbar, im Hintergrund gespeichert) */
  aendern: (teil: AnsichtEingabe) => void;
  /** zurück auf den Standard der Liste */
  zuruecksetzen: () => void;
}

/** Gemerkte Ansicht einer Liste. Meldet die Liste einmal je Öffnen als „genutzt“ (höchstens alle 20 Sekunden). */
export function useGemerkteAnsicht(liste: string, nutzungMelden = true): AnsichtZugriff {
  const [, neuZeichnen] = useState(0);
  const listeRef = useRef(liste);
  listeRef.current = liste;
  const geladen = ansichten.has(liste);

  useEffect(() => {
    let aktiv = true;
    const meine = sitzung;
    if (!ansichten.has(liste)) {
      void laden(liste).then(() => {
        if (aktiv && meine === sitzung) neuZeichnen((n) => n + 1);
      });
    }
    if (nutzungMelden) {
      const zuletzt = genutztAm.get(liste) ?? 0;
      if (Date.now() - zuletzt > 20_000) {
        genutztAm.set(liste, Date.now());
        void listenAnsichtenApi.genutzt(liste).catch(() => undefined);
      }
    }
    return () => {
      aktiv = false;
    };
  }, [liste, nutzungMelden]);

  const aendern = useCallback(
    (teil: AnsichtEingabe) => {
      const l = listeRef.current;
      const meine = sitzung;
      ansichten.set(l, eingemischt(ansichten.get(l) ?? null, l, teil));
      neuZeichnen((n) => n + 1);
      listenAnsichtenApi.merken(l, teil).then(
        (r) => {
          // Antwort des Servers übernehmen, solange nichts Neueres gewählt wurde
          if (meine === sitzung && !ladend.has(l) && ansichten.get(l)?.date_updated !== r.ansicht.date_updated) {
            const jetzt = ansichten.get(l);
            if (jetzt) ansichten.set(l, { ...jetzt, zuletzt_genutzt_at: r.ansicht.zuletzt_genutzt_at, nutzungen: r.ansicht.nutzungen, date_updated: r.ansicht.date_updated });
          }
        },
        () => undefined,
      );
    },
    [],
  );

  const zuruecksetzen = useCallback(() => {
    const l = listeRef.current;
    ansichten.set(l, eingemischt(ansichten.get(l) ?? null, l, { spalten: null, sortierung: null, einstellungen: null }));
    neuZeichnen((n) => n + 1);
    void listenAnsichtenApi.zuruecksetzen(l).catch(() => undefined);
  }, []);

  return { ansicht: ansichten.get(liste) ?? null, geladen, aendern, zuruecksetzen };
}

// ---------------------------------------------------------------------------
// Katalog der Spalten
// ---------------------------------------------------------------------------

function katalogHolen(): Promise<FelderKatalog | null> {
  if (katalog) return Promise.resolve(katalog);
  if (katalogLadend) return katalogLadend;
  const meine = sitzung;
  katalogLadend = listenAnsichtenApi
    .felderLeads()
    .then((k) => {
      if (meine === sitzung) katalog = k;
      return k;
    })
    .catch(() => null)
    .finally(() => {
      if (meine === sitzung) katalogLadend = null;
    });
  return katalogLadend;
}

/** Katalog der Lead-Spalten; null, solange er geladen wird oder wenn das Laden fehlschlägt (die Seiten funktionieren dann ohne Spaltenwahl). */
export function useFelderKatalog(): FelderKatalog | null {
  const [k, setK] = useState<FelderKatalog | null>(katalog);
  useEffect(() => {
    let aktiv = true;
    if (!katalog) void katalogHolen().then((r) => aktiv && r && setK(r));
    else setK(katalog);
    return () => {
      aktiv = false;
    };
  }, []);
  return k;
}

// ---------------------------------------------------------------------------
// Berechnete Spaltenwerte
// ---------------------------------------------------------------------------

const PAKET = 300;

function werteGeaendert() {
  werteStand++;
  werteHoerer.forEach((h) => h());
}

async function werteHolen(ids: string[], keys: string[]): Promise<void> {
  const meine = sitzung;
  for (let i = 0; i < ids.length; i += PAKET) {
    const teil = ids.slice(i, i + PAKET);
    try {
      const r = await listenAnsichtenApi.werteLeads(teil, keys);
      if (meine !== sitzung) return;
      for (const id of teil) {
        const alt = werte.get(id) ?? {};
        werte.set(id, { ...alt, ...(r.data[id] ?? {}) });
      }
    } catch {
      // Spalte bleibt leer (—); nach zehn Sekunden wird es noch einmal versucht (solange die Liste offen ist)
      if (meine !== sitzung) return;
      window.setTimeout(() => {
        if (meine !== sitzung) return;
        for (const id of teil) for (const k of keys) angefragt.delete(`${id}|${k}`);
        werteGeaendert();
      }, 10_000);
    }
    werteGeaendert();
  }
}

/**
 * Berechnete Werte (Anzahl Anrufe, letzte Kommunikation, nächste Aufgabe …) für `leadIds` und `keys`. Holt nur, was fehlt.
 * `wert(id, key)` liefert den Wert oder undefined (noch nicht da). `stand` ändert sich, wenn Werte angekommen sind (für useMemo).
 */
export function useBerechneteWerte(leadIds: readonly string[], keys: readonly string[]): { wert: (id: string, key: string) => unknown; stand: number; laedt: boolean } {
  const [stand, setStand] = useState(werteStand);
  useEffect(() => {
    const h = () => setStand(werteStand);
    werteHoerer.add(h);
    return () => {
      werteHoerer.delete(h);
    };
  }, []);

  const keysRef = useRef<readonly string[]>(keys);
  keysRef.current = keys;

  // Ändert ein Lead sich (Anruf, Aufgabe, Status …), werden seine Werte neu geholt
  useEchtzeit(["lead.updated", "activity.created", "activity.updated", "task.created", "task.updated"], (e) => {
    if (!e.lead_id || !werte.has(e.lead_id)) return;
    werte.delete(e.lead_id);
    for (const k of keysRef.current) angefragt.delete(`${e.lead_id}|${k}`);
    werteGeaendert();
  });

  const keyText = keys.join(",");
  const idText = useMemo(() => leadIds.join(","), [leadIds]);

  const [laeuft, setLaeuft] = useState(0);
  useEffect(() => {
    if (!keys.length || !leadIds.length) return;
    const fehlend = leadIds.filter((id) => keys.some((k) => !angefragt.has(`${id}|${k}`)));
    if (!fehlend.length) return;
    for (const id of fehlend) for (const k of keys) angefragt.add(`${id}|${k}`);
    setLaeuft((n) => n + 1);
    void werteHolen(fehlend, [...keys]).finally(() => setLaeuft((n) => n - 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyText, idText, stand]);

  const wert = useCallback((id: string, key: string): unknown => {
    const z = werte.get(id);
    return z && key in z ? z[key] : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stand]);

  return { wert, stand, laedt: laeuft > 0 };
}
